import { sdk } from '../sdk';
import { getErrorMessage } from '../utils/errorMessage';

export interface Donation {
  _id: string;
  receiptId: number;
  // Public, unguessable token used by the receipt QR / /verify page.
  verificationCode?: string;
  donorId: string;
  donorType: 'donor' | 'guestDonor';
  donorName: string;
  donorFatherOrHusbandName: string;
  donorRelation: 'Father' | 'Husband';
  donorAddress?: string;
  donorOfficeId: {
    _id: string;
    officeNo: number;
    name: string;
    id: string;
  };
  donorOfficeName: string;
  donorOfficeNo: number;
  ref?: string;
  amount: number;
  donationTypeId: {
    _id: string;
    name: string;
  };
  donationTypeName: string;
  cashierId: {
    _id: string;
    name: string;
    role: string;
    fullName: string;
    id: string;
  };
  cashierName: string;
  cashierRole: string;
  pcInfo: {
    pcName: string;
    pcId: string;
    macAddress?: string;
    ipAddress?: string;
  };
  isReceiptPrinted: boolean;
  receiptPrintedAt?: string;
  receiptPrintedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DonorDetails {
  id: string;
  donorType: 'donor' | 'guestDonor';
  name: string;
  fatherOrHusbandName: string;
  relation: 'Father' | 'Husband';
  address?: string;
  cnic?: string;
  officeId: string;
  officeName: string;
  officeNo: number;
  phone?: string;
  email?: string;
}

export interface CreateDonationData {
  donorId: string;
  donorType: 'donor' | 'guestDonor';
  donorName: string;
  donorFatherOrHusbandName: string;
  donorRelation: 'Father' | 'Husband';
  donorAddress?: string;
  donorOfficeId: string;
  donorOfficeName: string;
  donorOfficeNo: number;
  ref?: string;
  amount: number;
  donationTypeId: string;
  donationTypeName: string;
  cashierId: string;
  cashierName: string;
  cashierRole: string;
  pcInfo: {
    pcName: string;
    pcId: string;
    macAddress?: string;
    ipAddress?: string;
  };
}

export interface UpdateDonationData {
  ref?: string;
  amount?: number;
  donationTypeId?: string;
  donationTypeName?: string;
}

export interface DonationSearchParams {
  page?: number;
  limit?: number;
  search?: string;
  donorType?: string;
  officeId?: string;
  donationTypeId?: string;
  startDate?: string;
  endDate?: string;
  isReceiptPrinted?: string;
}

export interface DonationStats {
  totalDonations: number;
  totalAmount: number;
  avgAmount: number;
  printedReceipts: number;
  unprintedReceipts: number;
  uniqueDonors: number;
  uniqueOffices: number;
  topDonationTypes: Array<{
    _id: string;
    typeName: string;
    count: number;
    totalAmount: number;
  }>;
}

export class DonationsAPI {
  // Get donor details by ID and type
  static async getDonorDetails(donorId: string, donorType: 'donor' | 'guest'): Promise<DonorDetails> {
    try {
      const response = await sdk.client.get(`/api/v1/donations/donor-details/${donorType}/${donorId}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donor details');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch donor details'));
    }
  }

  // Create new donation
  static async createDonation(data: CreateDonationData): Promise<Donation> {
    try {
      const response = await sdk.client.post('/api/v1/donations', data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create donation');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to create donation'));
    }
  }

  // Get all donations with search and pagination
  static async getAllDonations(params: DonationSearchParams = {}): Promise<{
    data: Donation[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  }> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page.toString());
      // Default limit to 50 if not specified
      queryParams.append('limit', (params.limit || 50).toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.donorType) queryParams.append('donorType', params.donorType);
      if (params.officeId) queryParams.append('officeId', params.officeId);
      if (params.donationTypeId) queryParams.append('donationTypeId', params.donationTypeId);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.isReceiptPrinted) queryParams.append('isReceiptPrinted', params.isReceiptPrinted);

      const response = await sdk.client.get(`/api/v1/donations?${queryParams.toString()}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donations');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch donations'));
    }
  }

  // Get donation by ID
  static async getDonationById(id: string): Promise<Donation> {
    try {
      const response = await sdk.client.get(`/api/v1/donations/${id}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donation');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch donation'));
    }
  }

  // Update donation
  static async updateDonation(id: string, data: UpdateDonationData): Promise<Donation> {
    try {
      const response = await sdk.client.put(`/api/v1/donations/${id}`, data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update donation');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to update donation'));
    }
  }

  // Mark receipt as printed
  static async markReceiptPrinted(id: string, printedBy: string): Promise<Donation> {
    try {
      const response = await sdk.client.patch(`/api/v1/donations/${id}/mark-printed`, { printedBy });
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to mark receipt as printed');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to mark receipt as printed'));
    }
  }

  // Delete donation (for transaction rollback)
  static async deleteDonation(id: string): Promise<void> {
    try {
      const response = await sdk.client.delete(`/api/v1/donations/${id}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete donation');
      }
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to delete donation'));
    }
  }

  // Trigger the donor receipt email AFTER a confirmed successful print, so
  // rolled-back donations are never emailed. Best-effort: failures are swallowed
  // by the caller and never block the donation flow.
  static async sendReceiptEmail(id: string): Promise<void> {
    await sdk.client.post(`/api/v1/donations/${id}/send-receipt-email`, {});
  }

  static async confirmPrint(
    id: string,
    data: { printerName: string; printSuccess: boolean }
  ): Promise<void> {
    await sdk.client.patch(`/api/v1/donations/${id}/confirm-print`, data);
  }

  // Get donation statistics
  static async getDonationStats(params: {
    startDate?: string;
    endDate?: string;
    officeId?: string;
    donationTypeId?: string;
  } = {}): Promise<DonationStats> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.officeId) queryParams.append('officeId', params.officeId);
      if (params.donationTypeId) queryParams.append('donationTypeId', params.donationTypeId);

      const response = await sdk.client.get(`/api/v1/donations/stats?${queryParams.toString()}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch donation stats');
      }
      
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch donation stats'));
    }
  }
}
